import { all, put, takeLatest } from 'redux-saga/effects';
import {
  DEACTIVE,
  DEACTIVE_SUCCESS,
  DELETE,
  DELETE_SUCCESS,
  GET_CATEGORIES,
  GET_CATEGORIES_SUCCESS,
  GET_CATEGORY_FILE_SUB_TYPE,
  GET_CATEGORY_FILE_SUB_TYPE_SUCCESS,
  GET_CATEGORY_LIST,
  GET_CATEGORY_LIST_SUCCESS,
  GET_DETAIL,
  GET_DETAIL_SUCCESS,
  GET_REFERENCE_DATA_DETAIL,
  GET_REFERENCE_DATA_DETAIL_SUCCESS,
  SET_LOADING,
  SET_UPDATING_CATEGORY,
  SUBMIT_DATA,
  SUBMIT_DATA_SUCCESS,
  UPDATE,
} from '../types/category';

import {
  createCategoryApi,
  deleteCategoryApi,
  deleteCategoryFilesApi,
  requestCategoriesNestedApi,
  requestCategoryDetailApi,
  requestCategoryFilesApi,
  requestCategoryFileSubTypeApi,
  requestCategoryTypesApi,
  requestGetCategories,
  requestPatchCategory,
  updateCategoryApi,
  updateCategoryFilesApi,
  uploadFileCategoryApi,
} from '../requests/category';

import { onFailRequest, onSuccessRequest } from '@/utils';

/**
 * Get categories with pagination
 * @param {*} payload
 */
function* requestCategoriesNested({ payload }) {
  try {
    const response = yield requestCategoriesNestedApi(payload?.params);
    yield put({ type: GET_CATEGORIES_SUCCESS, data: response });
  } catch (error) {
    yield onFailRequest({ payload, error });
  }
}

function* getCategoryList({ payload }) {
  try {
    const { data } = yield requestGetCategories(payload);
    yield put({ type: GET_CATEGORY_LIST_SUCCESS, payload: data });
  } catch (error) {
    yield onFailRequest({ error });
  }
}

/**
 * Get reference data of category
 * @param {*} payload
 */
function* requestReferenceData(payload) {
  try {
    const data = yield all([requestCategoryTypesApi({})]);
    yield put({
      type: GET_REFERENCE_DATA_DETAIL_SUCCESS,
      result: { categoryTypes: data[0].data },
    });
  } catch (error) {
    yield onFailRequest({
      payload,
      error,
      *onCallback() {
        yield put({ type: SET_LOADING, isLoading: false });
      },
    });
  }
}

/**
 * Get detail data category
 * @param {*} payload
 */
function* requestCategoryDetail(payload) {
  try {
    const { params } = payload;
    const [category, categoryFiles] = yield all([
      requestCategoryDetailApi(params),
      requestCategoryFilesApi(params.id),
    ]);
    let data = category;
    if (category && categoryFiles.data) {
      data = {
        ...category,
        files: categoryFiles.data,
      };
    }
    yield put({
      type: GET_DETAIL_SUCCESS,
      data,
    });
  } catch (error) {
    yield onFailRequest({
      payload,
      error,
      *onCallback() {
        yield put({ type: SET_LOADING, isLoading: false });
      },
    });
  }
}

/**
 * Get reference data for body submit form when create & update category
 * @param {*} param0
 * @returns {}
 */
function getRefDataToSubmit({ categoryId = 0, files = [] }) {
  try {
    const newFileActions = [];
    const deleteFileActions = [];
    const updateFileActions = [];
    files.forEach((item) => {
      const {
        id: fileId,
        originFileObj,
        isDeleted = false,
        isNew = false,
        // isUpdated = false,
        display_priority,
      } = item;
      if (originFileObj && isNew) {
        // Upload file
        newFileActions.push(
          uploadFileCategoryApi(categoryId, {
            file: originFileObj,
            file_type: 'image',
            file_sub_type: item.file_sub_type,
            channel: item.channel || undefined,
            display_priority,
          }),
        );
      }
      // Call request delete file
      if (isDeleted && fileId) {
        deleteFileActions.push(deleteCategoryFilesApi(categoryId, fileId));
      }
      // Call request update file
      if (!isDeleted && !isNew && fileId) {
        const body = {
          channel: item.channel || undefined,
          is_active: true,
          _method: 'PUT',
        };
        ['file_type', 'file_sub_type', 'display_priority'].forEach((key) => {
          body[key] = item[key];
        });
        updateFileActions.push(updateCategoryFilesApi(categoryId, fileId, body));
      }
    });
    return [...newFileActions, ...deleteFileActions, ...updateFileActions];
  } catch (err) {
    return [];
  }
}

/**
 * Submit data to API
 * @param {*} payload
 */
function* submitCategory(payload) {
  const { params, callback } = payload;
  try {
    const { id = 0, body, files = [] } = params;
    let categoryId = id;
    let refParams = { categoryId, files };
    const actions = [];
    let result = null;
    if (categoryId) {
      // Update category
      actions.push(updateCategoryApi(categoryId, body));
    } else {
      // Create new category
      result = yield createCategoryApi(body);
      const { data: responseData } = result;
      categoryId = responseData.id;
      if (result) {
        refParams = { ...refParams, categoryId };
        actions.push(result);
      }
    }
    const [resUpdate] = yield all([...actions, ...getRefDataToSubmit(refParams)]);
    result = resUpdate;
    yield requestCategoryDetail({ params: { id: categoryId } });
    yield onSuccessRequest({
      response: result,
    });

    yield put({ type: SUBMIT_DATA_SUCCESS });
    callback(result);
  } catch (error) {
    yield onFailRequest({
      payload,
      error,
      *onCallback() {
        yield put({ type: SET_UPDATING_CATEGORY, payload: false });
      },
    });
  }
}

/**
 * Delete category
 * @param {*} payload
 */
function* deleteCategory(payload) {
  const { id, callback } = payload;
  try {
    const result = yield deleteCategoryApi(id);
    yield put({
      type: DELETE_SUCCESS,
    });
    yield onSuccessRequest({
      response: result,
      *onCallback() {
        yield callback(true);
      },
    });
  } catch (error) {
    yield onFailRequest({
      payload,
      error,
      *onCallback() {
        yield callback(false);
      },
    });
  }
}

function* deactiveCategory(payload) {
  const { id, callback } = payload;
  try {
    const res = yield requestPatchCategory(id, { is_active: false });
    yield put({ type: DEACTIVE_SUCCESS, payload: res?.data });
    yield onSuccessRequest({
      response: res,
      *onCallback() {
        yield callback(true);
      },
    });
  } catch (error) {
    yield onFailRequest({
      payload,
      error,
      *onCallback() {
        yield callback(false);
      },
    });
  }
}

/**
 * Update category
 * @param {*} payload
 */
function* updateCategory(payload) {
  try {
    const { params } = payload;
    const { categoryId = 0, body } = params;
    yield updateCategoryApi(categoryId, body);
  } catch (error) {
    yield onFailRequest({
      payload,
      error,
    });
  }
}

/**
 * Get category file sub types
 * @param {*} payload
 */
function* getCategoryFileSubTypes(payload) {
  try {
    const { data } = yield requestCategoryFileSubTypeApi();
    yield put({ type: GET_CATEGORY_FILE_SUB_TYPE_SUCCESS, payload: data });
  } catch (error) {
    yield onFailRequest({
      payload,
      error,
    });
  }
}

export default [
  takeLatest(GET_CATEGORIES, requestCategoriesNested),
  takeLatest(GET_CATEGORY_LIST, getCategoryList),
  takeLatest(GET_DETAIL, requestCategoryDetail),
  takeLatest(GET_REFERENCE_DATA_DETAIL, requestReferenceData),
  takeLatest(SUBMIT_DATA, submitCategory),
  takeLatest(DELETE, deleteCategory),
  takeLatest(UPDATE, updateCategory),
  takeLatest(GET_CATEGORY_FILE_SUB_TYPE, getCategoryFileSubTypes),
  takeLatest(DEACTIVE, deactiveCategory),
];
